/**
 * Shared league sorting / classification helpers.
 *
 * Used by both `Dashboard.jsx` (Home) and `IAPage.jsx` (AI ticket generator)
 * so the schedule looks identical: same country labels, same TOP-league
 * grouping (expanded by default) and same sort order.
 */

// Translate API-Football country codes to Italian labels. Continental buckets
// at the bottom — they appear next to UEFA / FIFA competitions.
export const COUNTRY_LABEL_IT = {
  Italy: 'Italia', England: 'Inghilterra', Spain: 'Spagna',
  Germany: 'Germania', France: 'Francia', Netherlands: 'Paesi Bassi',
  Portugal: 'Portogallo', Belgium: 'Belgio', Turkey: 'Turchia',
  Scotland: 'Scozia', Austria: 'Austria', Greece: 'Grecia',
  Switzerland: 'Svizzera', Denmark: 'Danimarca', Norway: 'Norvegia',
  Sweden: 'Svezia', Finland: 'Finlandia', Poland: 'Polonia',
  'Czech-Republic': 'Repubblica Ceca', Russia: 'Russia', Ukraine: 'Ucraina',
  Croatia: 'Croazia', Serbia: 'Serbia', Slovakia: 'Slovacchia',
  Slovenia: 'Slovenia', Hungary: 'Ungheria', Romania: 'Romania',
  Bulgaria: 'Bulgaria', Iceland: 'Islanda', Ireland: 'Irlanda',
  Wales: 'Galles', 'Northern-Ireland': 'Irlanda del Nord',
  USA: 'USA', Mexico: 'Messico', Brazil: 'Brasile',
  Argentina: 'Argentina', Chile: 'Cile', Uruguay: 'Uruguay',
  Colombia: 'Colombia', Japan: 'Giappone', China: 'Cina',
  'South-Korea': 'Corea del Sud', Australia: 'Australia',
  'Saudi-Arabia': 'Arabia Saudita', UAE: 'Emirati Arabi',
  Qatar: 'Qatar', Egypt: 'Egitto', Morocco: 'Marocco',
  Europe: 'Europa', World: 'Mondo', 'South America': 'Sud America',
  'North America': 'Nord America', Asia: 'Asia', Africa: 'Africa',
};

export const translateCountry = (country) =>
  !country ? '' : (COUNTRY_LABEL_IT[country] || country);

// TOP LEAGUES allowlist — substring match on league name per country.
// Anything matched here is shown EXPANDED by default and pinned to the top.
//
// IMPORTANT: the ORDER inside each country's array also determines the
// display order on Home/IA when multiple top leagues from the same country
// are present. Always list the national cup immediately AFTER the top
// division so users see e.g. "Serie A → Coppa Italia → Serie B".
export const TOP_LEAGUE_RULES = {
  Italy: ['Serie A', 'Coppa Italia', 'Serie B', 'Serie C', 'Serie D'],
  England: ['Premier League', 'FA Cup', 'EFL Cup', 'League Cup', 'Championship', 'League One', 'League Two'],
  Spain: ['La Liga', 'LaLiga', 'Primera Division', 'Copa del Rey', 'Segunda'],
  Germany: ['Bundesliga', 'DFB-Pokal', '2. Bundesliga'],
  France: ['Ligue 1', 'Coupe de France', 'Ligue 2', 'National 1', 'National'],
  Netherlands: ['Eredivisie', 'KNVB Beker', 'Eerste Divisie'],
  Portugal: ['Primeira Liga', 'Liga Portugal', 'Taça de Portugal', 'Taca de Portugal', 'Segunda Liga', 'Liga 2'],
  Belgium: ['Jupiler Pro League', 'Pro League', 'Croky Cup', 'Belgian Cup', 'Cup', 'Challenger Pro League', 'First Division', 'Second Division'],
  Scotland: ['Premiership', 'Scottish Cup', 'Scottish League Cup', 'Championship', 'League One', 'League Two'],
  Wales: ['Premier League', 'Cymru Premier', 'Welsh Cup'],
  Austria: ['Bundesliga', 'ÖFB-Cup', 'OFB-Cup', 'Austrian Cup', '2. Liga'],
  Norway: ['Eliteserien', 'NM Cupen', 'Norwegian Cup', '1. Division', 'OBOS-ligaen'],
  Sweden: ['Allsvenskan', 'Svenska Cupen', 'Superettan'],
  Denmark: ['Superliga', 'Superligaen', 'DBU Pokalen', 'Danish Cup', '1st Division', '1. Division', 'NordicBet Liga'],
  Turkey: ['Super Lig', 'Süper Lig', 'Türkiye Kupası', 'Turkish Cup', '1. Lig'],
  Switzerland: ['Super League', 'Challenge League', 'Schweizer Cup', 'Swiss Cup'],
  Greece: ['Super League', 'Greek Cup'],
  Croatia: ['HNL', 'SuperSport HNL', 'Prva HNL', 'Croatian Cup', 'Hrvatski Kup', 'First NL'],
  Iceland: ['Úrvalsdeild', 'Urvalsdeild'],
  Ireland: ['Premier Division', 'First Division', 'FAI Cup'],
  Brazil: ['Serie A', 'Copa do Brasil', 'Serie B'],
  Argentina: ['Liga Profesional', 'Primera Division', 'Primera División', 'Copa Argentina'],
  Europe: [
    'UEFA Champions League', 'Europa League', 'Conference League', 'Super Cup',
    'European Championship', 'Nations League',
    'U21', 'U19', 'U17',
  ],
  World: [
    'World Cup', 'FIFA',
    'UEFA Champions League', 'Europa League', 'Conference League',
    'UEFA Super Cup', 'Nations League',
    'Copa America',
    'Copa Libertadores', 'Copa Sudamericana',
    'CONMEBOL Libertadores', 'CONMEBOL Sudamericana', 'CONMEBOL',
  ],
  'South-America': ['Copa Libertadores', 'Copa Sudamericana', 'Copa America'],
};

// Hard exclusions that override TOP allowlist matches (women / youth / reserve
// teams / Spanish amateur tiers).
export const NON_TOP_KEYWORDS = [
  'Dama', 'Women', 'Ladies', 'Femminile', 'Femenin', 'Frauen',
  'U23', 'U21', 'U20', 'U19', 'U18', 'U17', 'U16', 'U15',
  'Reserve', 'Reservas', 'Youth', 'Junior', 'Sub-',
  'RFEF',
];

// Admin overrides for the `is_top` flag, loaded from
// GET /api/league-top-overrides at app startup. Map: league_id (string) → bool.
// `setLeagueTopOverrides({...})` is called by `App.js` once the fetch resolves.
let LEAGUE_TOP_OVERRIDES = {};
export const setLeagueTopOverrides = (map) => {
  LEAGUE_TOP_OVERRIDES = (map && typeof map === 'object') ? map : {};
};
export const getLeagueTopOverrides = () => LEAGUE_TOP_OVERRIDES;

// Admin PIN overrides (2026-08-08). Pinned leagues sort GLOBALLY at the top
// by priority — they escape the alphabetical nation grouping. Loaded from
// GET /api/league-pins at app startup. Map: league_id (string) → true.
let LEAGUE_PIN_OVERRIDES = {};
export const setLeaguePinOverrides = (map) => {
  LEAGUE_PIN_OVERRIDES = (map && typeof map === 'object') ? map : {};
};
export const getLeaguePinOverrides = () => LEAGUE_PIN_OVERRIDES;

// Admin-curated absolute ordering (loaded from GET /api/league-manual-orders).
// When set, the league sorts strictly by this index — overrides TOP-first,
// priority, country bucket, everything. Map: league_id (string) → int.
let LEAGUE_MANUAL_ORDERS = {};
export const setLeagueManualOrders = (map) => {
  LEAGUE_MANUAL_ORDERS = (map && typeof map === 'object') ? map : {};
};
export const getLeagueManualOrders = () => LEAGUE_MANUAL_ORDERS;

// Returns TRUE if the league is a "code-defined TOP league" (matches
// TOP_LEAGUE_RULES for its country and has no youth/women disqualifier).
// PURE code logic — does NOT consult admin overrides. Admin chip
// APERTA/CHIUSA affects ONLY the accordion default via `getLeagueTopOverrides()`
// and NEVER pushes a league to the top of the list (2026-08-08 user request).
export const isTopLeague = (leagueName, country /*, leagueId (deprecated) */) => {
  if (!leagueName) return false;
  const lnLower = leagueName.toLowerCase();
  if (NON_TOP_KEYWORDS.some(kw => lnLower.includes(kw.toLowerCase()))) {
    if (country !== 'Europe' && country !== 'World') return false;
  }
  const rules = TOP_LEAGUE_RULES[country];
  if (!rules) return false;
  return rules.some(kw => lnLower.includes(kw.toLowerCase()));
};

// Returns the position of the league within its country's TOP_LEAGUE_RULES
// array — used as a tiebreaker so e.g. "Coppa Italia" sits between Serie A
// and Serie B. Returns 999 if no match (non-top leagues).
export const getTopLeagueOrder = (leagueName, country) => {
  if (!leagueName) return 999;
  const rules = TOP_LEAGUE_RULES[country];
  if (!rules) return 999;
  const lnLower = leagueName.toLowerCase();
  const idx = rules.findIndex(kw => lnLower.includes(kw.toLowerCase()));
  return idx === -1 ? 999 : idx;
};

export const getLeagueKey = (m) => {
  const name = m.league_name || 'Altro';
  const country = m.league_country || '';
  return country ? `${name}___${country}` : name;
};

// "Bundesliga (Germania)" — always show the country suffix so closed leagues
// are visually grouped by nation (e.g. Coppa Italia → "Coppa Italia (Italia)").
// Only skip the suffix when the translated label equals the whole league name
// (avoids nonsensical duplicates like "Italia (Italia)").
export const getLeagueDisplay = (leagueName, country) => {
  if (!country) return leagueName;
  const label = translateCountry(country);
  if (leagueName.trim().toLowerCase() === label.toLowerCase()) return leagueName;
  return `${leagueName} (${label})`;
};

/**
 * Group matches by (league + country), sort top leagues first, then by
 * priority. Within each group matches stay in the original order.
 *
 * Returns: [{ key, name, country, displayName, logo, matches, isTop, priority }]
 */
export const buildLeagueGroups = (matches) => {
  const map = new Map();
  const pinOverrides = getLeaguePinOverrides();
  for (const match of matches) {
    const key = getLeagueKey(match);
    if (!map.has(key)) {
      const country = match.league_country || '';
      const name = match.league_name || 'Altro';
      const priority = (match.priority == null ? 999 : match.priority);
      const leagueId = match.league_id;
      const leagueIdKey = leagueId != null ? String(leagueId) : null;
      // Admin overrides for `is_top`:
      //   true  → forza isTop=true (apre tendina + sposta in cima)
      //   false → tendina chiusa di default, posizione invariata (isTop dal codice)
      //   absent → tutto da regole codice
      const topOverrides = getLeagueTopOverrides();
      const override = leagueIdKey && leagueIdKey in topOverrides ? topOverrides[leagueIdKey] : null;
      // PIN globale (2026-08-08): la lega esce dal raggruppamento nazione
      // e va in cima ordinata per priority. Settato dall'admin via input
      // numerico posizione nel pannello Priorità Leghe.
      const isPinned = !!(leagueIdKey && pinOverrides[leagueIdKey]);
      const codebaseIsTop = priority < 200 && isTopLeague(name, country, leagueId);
      // POSIZIONE (`isTop`): pinned → sempre in cima. Se non pinned e admin
      // ha toccato il chip (APERTA o CHIUSA), niente auto-pin. Altrimenti
      // codebase default.
      const isTop = isPinned ? true : (override != null ? false : codebaseIsTop);
      // TENDINA (`defaultOpen`): controllata dall'admin override quando presente,
      // altrimenti segue il codebase default. Se pinned, default aperto.
      const defaultOpen = override === true ? true
        : override === false ? false
        : (isPinned ? true : codebaseIsTop);
      map.set(key, {
        key,
        name,
        country,
        displayName: getLeagueDisplay(name, country),
        logo: match.league_logo,
        matches: [],
        isTop,
        isPinned,
        defaultOpen,
        priority,
      });
    }
    map.get(key).matches.push(match);
  }
  const groups = Array.from(map.values());
  // Sort:
  //   1. PINNED prima di tutto (globalmente, priority ASC)
  //   2. Non-pinned aperte prima delle chiuse (chip AUTO su code-TOP)
  //   3. Aperte tra loro: priority ASC → name
  //   4. Chiuse: raggruppate per nazione (label ITALIANA alphabetical),
  //      poi priority ASC dentro ogni gruppo nazione.
  groups.sort((a, b) => {
    // 1) pinned first
    if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
    if (a.isPinned && b.isPinned) {
      if (a.priority !== b.priority) return a.priority - b.priority;
      return a.name.localeCompare(b.name);
    }
    // 2) aperte vs chiuse
    if (a.isTop !== b.isTop) return a.isTop ? -1 : 1;
    // 3) entrambe aperte: sort admin-driven (priority + name)
    if (a.isTop && b.isTop) {
      if (a.priority !== b.priority) return a.priority - b.priority;
      return a.name.localeCompare(b.name);
    }
    // 4) entrambe chiuse: raggruppa per country asc (label ITALIANA), poi priority asc
    const ca = translateCountry(a.country || '').toLowerCase();
    const cb = translateCountry(b.country || '').toLowerCase();
    if (ca !== cb) return ca.localeCompare(cb);
    if (a.priority !== b.priority) return a.priority - b.priority;
    return a.name.localeCompare(b.name);
  });
  return groups;
};
